import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search, BarChart2, Activity, ClipboardList, Lock, Info,
  AlertTriangle, CheckCircle, XCircle, ChevronLeft, ChevronRight,
  BookOpen, ExternalLink, RefreshCw,
} from '../components/Icons';
import {
  STEPS, STEP_META,
  GI_QUESTIONS, GSSS_QUESTIONS, GSSS_OPTIONS, FLACC_CATEGORIES,
  getGIInterp, getGSSSInterp, getFLACCInterp, getFollowUp, REFERENCES,
} from './screeningData';
import styles from './ScreeningFlow.module.css';

// map tone → class warna
const toneClass = (tone) =>
  tone === 'danger' ? styles.toneDanger : tone === 'warn' ? styles.toneWarn : styles.toneGood;

function ScreeningFlow({
  mode = 'public',          // 'public' | 'auth'
  childrenList = [],         // daftar anak (mode auth)
  selectedChild = '',
  onSelectChild = () => {},
  onComplete = null,         // (scores) => Promise — simpan ke DB (mode auth)
  onRegister = () => {},     // navigasi ke /register
  onLogin = () => {},        // navigasi ke /login
}) {
  const { i18n } = useTranslation();
  const isID = i18n.language === 'id';
  const isPublic = mode === 'public';

  const [step, setStep] = useState('intro');   // intro | gi | gsss | flacc | result
  // Gulir ke atas tiap kali langkah berganti, agar pengguna langsung
  // melihat pertanyaan pertama (bukan posisi gulir langkah sebelumnya).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);
  const [giAnswers, setGiAnswers] = useState({});
  const [gsssAnswers, setGsssAnswers] = useState({});
  const [flaccAnswers, setFlaccAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const giComplete = Object.keys(giAnswers).length === GI_QUESTIONS.length;
  const gsssComplete = Object.keys(gsssAnswers).length === GSSS_QUESTIONS.length;
  const flaccComplete = Object.keys(flaccAnswers).length === FLACC_CATEGORIES.length;

  const reset = () => {
    setGiAnswers({}); setGsssAnswers({}); setFlaccAnswers({});
    setResult(null); setStep('intro');
  };

  const computeScores = () => ({
    giScore: Object.values(giAnswers).filter(v => v === 'Ya').length,
    gsssScore: Object.values(gsssAnswers).reduce((s, v) => s + v, 0),
    flaccScore: Object.values(flaccAnswers).reduce((s, v) => s + v, 0),
  });

  // Bangun jawaban LENGKAP (snapshot teks pertanyaan + jawaban)
  // agar bisa dibaca ulang walau pertanyaan berubah di masa depan.
  const buildAnswers = (scores) => {
    const giOpt = (val) => (val === 'Ya' ? (isID ? 'Ya' : 'Yes') : (isID ? 'Tidak' : 'No'));
    const gsssOptLabel = (v) => {
      const o = GSSS_OPTIONS.find(x => x.value === v);
      return o ? (isID ? o.label_id : o.label_en) : String(v);
    };
    const flaccOptLabel = (cat, v) => {
      const o = cat.options.find(x => x.value === v);
      return o ? (isID ? o.label_id : o.label_en) : String(v);
    };

    return {
      scores: { gi: scores.giScore, gsss: scores.gsssScore, flacc: scores.flaccScore },
      gi: GI_QUESTIONS.map(q => ({
        q: isID ? q.text_id : q.text_en,
        a: giAnswers[q.id] != null ? giOpt(giAnswers[q.id]) : '-',
      })),
      gsss: GSSS_QUESTIONS.map(q => ({
        q: isID ? q.text_id : q.text_en,
        a: gsssAnswers[q.id] != null ? gsssOptLabel(gsssAnswers[q.id]) : '-',
        value: gsssAnswers[q.id] ?? null,
      })),
      flacc: FLACC_CATEGORIES.map(cat => ({
        q: isID ? cat.label_id : cat.label_en,
        a: flaccAnswers[cat.id] != null ? flaccOptLabel(cat, flaccAnswers[cat.id]) : '-',
        value: flaccAnswers[cat.id] ?? null,
      })),
    };
  };

  const handleFinish = async () => {
    const scores = computeScores();
    const answers = buildAnswers(scores);   // ← snapshot lengkap
    if (onComplete) {
      try { setSaving(true); await onComplete(scores, answers); }  // ← kirim 2 argumen
      finally { setSaving(false); }
    }
    setResult(scores);
    setStep('result');
  };

  // ===== STEP INDICATOR (baca dari STEPS — satu sumber kebenaran) =====
  const StepIndicator = ({ current }) => {
    const currentIdx = STEPS.indexOf(current);
    return (
      <ol className={styles.steps}>
        {STEPS.map((key, i) => {
          const meta = STEP_META[key];
          const done = i <= currentIdx;
          return (
            <li key={key} className={styles.step}>
              <span className={`${styles.stepCircle} ${done ? styles.stepCircleActive : ''}`}>
                {meta.num}
              </span>
              <span className={`${styles.stepLabel} ${done ? styles.stepLabelActive : ''}`}>
                {isID ? meta.label_id : meta.label_en}
              </span>
            </li>
          );
        })}
      </ol>
    );
  };

  // banner mode publik
  const PublicBanner = () => isPublic && (
    <div className={styles.banner}>
      <Lock size={16} aria-hidden="true" />
      <span className={styles.bannerText}>
        {isID
          ? 'Mode publik — skor tidak tersimpan. Daftar untuk menyimpan & memantau riwayat.'
          : 'Public mode — scores are not saved. Register to save & track history.'}
      </span>
      <button className={styles.bannerBtn} onClick={onRegister}>
        {isID ? 'Daftar Gratis' : 'Sign Up Free'}
      </button>
    </div>
  );

  // navigasi generik: tahu langkah sebelum/sesudah dari posisi di STEPS
  const NavRow = ({ current, complete }) => {
    const idx = STEPS.indexOf(current);
    const prev = idx === 0 ? 'intro' : STEPS[idx - 1];
    const isLast = idx === STEPS.length - 1;
    const next = isLast ? null : STEPS[idx + 1];
    const remaining = current === 'gi'
      ? GI_QUESTIONS.length - Object.keys(giAnswers).length
      : current === 'gsss'
      ? GSSS_QUESTIONS.length - Object.keys(gsssAnswers).length
      : FLACC_CATEGORIES.length - Object.keys(flaccAnswers).length;

    return (
      <>
        <div className={styles.navRow}>
          <button className={styles.navBtn} onClick={() => setStep(prev)}>
            <ChevronLeft size={16} aria-hidden="true" />
            {isID ? 'Kembali' : 'Back'}
          </button>
          {isLast ? (
            <button className={styles.navBtnPrimary} disabled={!complete || saving}
              onClick={handleFinish}>
              {saving
                ? (isID ? 'Menyimpan…' : 'Saving…')
                : (isID ? 'Lihat Hasil' : 'See Results')}
              <CheckCircle size={16} aria-hidden="true" />
            </button>
          ) : (
            <button className={styles.navBtnPrimary} disabled={!complete}
              onClick={() => setStep(next)}>
              {isID ? 'Lanjut' : 'Next'}
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          )}
        </div>
        {!complete && (
          <p className={styles.warnText}>
            {isID
              ? `${remaining} pertanyaan belum dijawab`
              : `${remaining} question(s) unanswered`}
          </p>
        )}
      </>
    );
  };

  // ===================== INTRO =====================
  if (step === 'intro') {
    const tools = [
      { n: 1, name: isID ? 'Skrining Awal Pencernaan' : 'Initial GI Screening',
        desc: isID ? '12 pertanyaan untuk melihat faktor risiko gangguan pencernaan.' : '12 questions to identify GI risk factors.' },
      { n: 2, name: isID ? 'Tingkat Keparahan Gejala (GSSS)' : 'Symptom Severity (GSSS)',
        desc: isID ? 'Mengukur keparahan 7 gejala pencernaan dalam 7 hari terakhir.' : 'Measures severity of 7 GI symptoms in the last 7 days.' },
      { n: 3, name: isID ? 'Tingkat Nyeri (FLACC)' : 'Pain Level (FLACC)',
        desc: isID ? 'Menilai rasa nyeri anak berdasarkan 5 dimensi perilaku.' : 'Assesses pain via 5 behavioral dimensions.' },
    ];
    const noChild = mode === 'auth' && childrenList.length === 0;

    return (
      <div className={styles.page}>
        <PublicBanner />
        <h1 className={styles.title}>
          <Search size={26} aria-hidden="true" />
          {isID ? 'Tes Skrining Pencernaan (GI)' : 'GI Screening Test'}
        </h1>

        <div className={styles.introCard}>
          <div className={styles.introIcon}><ClipboardList size={32} aria-hidden="true" /></div>
          <h2 className={styles.introTitle}>
            {isID ? 'Skrining Pencernaan Menyeluruh' : 'Comprehensive GI Screening'}
          </h2>
          <p className={styles.introDesc}>
            {isID
              ? 'Terdiri dari 3 alat ukur tervalidasi: Skrining Awal, Keparahan Gejala (GSSS), dan Tingkat Nyeri (FLACC).'
              : 'Three validated tools: Initial Screening, Symptom Severity (GSSS), and Pain Level (FLACC).'}
          </p>

          <div className={styles.toolGrid}>
            {tools.map(t => (
              <div key={t.n} className={styles.toolCard}>
                <span className={styles.toolNum}>{t.n}</span>
                <span className={styles.toolText}>
                  <span className={styles.toolName}>{t.name}</span>
                  <span className={styles.toolDesc}>{t.desc}</span>
                </span>
              </div>
            ))}
          </div>

          {/* pilih anak — hanya mode auth */}
          {mode === 'auth' && childrenList.length > 0 && (
            <div className={styles.field}>
              <label className={styles.label} htmlFor="child-select">
                {isID ? 'Pilih Anak' : 'Select Child'}
              </label>
              <select id="child-select" className={styles.select}
                value={selectedChild} onChange={e => onSelectChild(e.target.value)}>
                {childrenList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          {noChild && (
            <div className={styles.warnNote}>
              <AlertTriangle size={18} aria-hidden="true" />
              {isID ? 'Tambahkan profil anak terlebih dahulu.' : 'Please add a child profile first.'}
            </div>
          )}

          <button className={styles.startBtn} disabled={noChild}
            onClick={() => { reset(); setStep('gi'); }}>
            {isID ? 'Mulai Tes' : 'Start Test'}
            <ChevronRight size={18} aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  // ===================== GI =====================
  if (step === 'gi') return (
    <div className={styles.page}>
      <PublicBanner />
      <StepIndicator current="gi" />
      <h1 className={styles.title}>
        <Search size={24} aria-hidden="true" />
        {isID ? 'Skrining Awal Pencernaan' : 'Initial GI Screening'}
      </h1>

      <div className={styles.infoBox}>
        <div className={styles.infoTitle}><Info size={15} aria-hidden="true" />
          {isID ? 'Tentang Tes Ini' : 'About This Test'}</div>
        <p className={styles.infoText}>
          {isID
            ? 'Membantu mengidentifikasi faktor risiko & tanda umum gangguan pencernaan. Jawaban bukan diagnosis, tetapi membantu menilai perlunya evaluasi lanjut.'
            : 'Helps identify GI risk factors & common warning signs. Answers are not a diagnosis but help assess whether further evaluation is needed.'}
        </p>
        <div className={`${styles.warnNote}`}>
          <AlertTriangle size={18} aria-hidden="true" />
          {isID
            ? 'Segera cari bantuan medis bila ada muntah darah, BAB hitam, nyeri berat, atau tanda dehidrasi.'
            : 'Seek urgent care for bloody vomit, black stool, severe pain, or dehydration signs.'}
        </div>
        <p className={styles.sourceNote}>
          <BookOpen size={13} aria-hidden="true" />
          {isID ? 'Referensi: Buie et al. (2010)' : 'Reference: Buie et al. (2010)'}
        </p>
      </div>

      <div className={styles.questionsCard}>
        {GI_QUESTIONS.map((q, i) => (
          <div key={q.id} className={styles.qItem}>
            <span className={styles.qCategory}>{q.category}</span>
            <div className={styles.qText}>
              <span className={styles.qNum}>{i + 1}.</span>
              {isID ? q.text_id : q.text_en}
            </div>
            <div className={styles.answerRow}>
              {['Ya', 'Tidak'].map(opt => {
                const sel = giAnswers[q.id] === opt;
                const cls = sel ? (opt === 'Ya' ? styles.answerYes : styles.answerNo) : '';
                return (
                  <button key={opt} className={`${styles.answerBtn} ${cls}`}
                    onClick={() => setGiAnswers(p => ({ ...p, [q.id]: opt }))}>
                    {opt === 'Ya'
                      ? <XCircle size={16} aria-hidden="true" />
                      : <CheckCircle size={16} aria-hidden="true" />}
                    {opt === 'Ya' ? (isID ? 'Ya' : 'Yes') : (isID ? 'Tidak' : 'No')}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <NavRow current="gi" complete={giComplete} />
    </div>
  );

  // ===================== GSSS =====================
  if (step === 'gsss') return (
    <div className={styles.page}>
      <PublicBanner />
      <StepIndicator current="gsss" />
      <h1 className={styles.title}>
        <BarChart2 size={24} aria-hidden="true" />
        {isID ? 'Keparahan Gejala (GSSS)' : 'Symptom Severity (GSSS)'}
      </h1>

      <div className={styles.infoBox}>
        <div className={styles.infoTitle}><Info size={15} aria-hidden="true" />
          {isID ? 'Tentang Tes Ini' : 'About This Test'}</div>
        <p className={styles.infoText}>
          {isID ? (
            <>Mengukur seberapa berat gejala pencernaan anak <strong className={styles.emphasis}>dalam 7 hari terakhir</strong>. Pilih angka yang paling sesuai dengan yang Anda amati.</>
          ) : (
            <>Measures how severe your child's GI symptoms were <strong className={styles.emphasis}>in the last 7 days</strong>. Choose the number that best matches what you observe.</>
          )}
        </p>
        <div className={styles.scaleGuide}>
          {GSSS_OPTIONS.map((o, idx) => (
            <div key={o.value} className={`${styles.scaleItem} ${
              idx === 0 ? styles.toneGood : idx < 3 ? styles.toneWarn : styles.toneDanger}`}>
              <span className={styles.scaleNum}>{o.value}</span>
              <span className={styles.scaleLabel}>{isID ? o.label_id : o.label_en}</span>
            </div>
          ))}
        </div>
        <p className={styles.sourceNote}>
          <BookOpen size={13} aria-hidden="true" />
          {isID ? 'Referensi: Skala GSSS tervalidasi (Rome IV)' : 'Reference: Validated GSSS (Rome IV)'}
        </p>
      </div>

      <div className={styles.questionsCard}>
        {GSSS_QUESTIONS.map((q, i) => (
          <div key={q.id} className={styles.qItem}>
            <div className={styles.qText}>
              <span className={styles.qNum}>{i + 1}.</span>
              {isID ? q.text_id : q.text_en}
            </div>
            <div className={styles.scoreRow}>
              {GSSS_OPTIONS.map(opt => {
                const sel = gsssAnswers[q.id] === opt.value;
                const selCls = sel
                  ? [styles.scoreSel0, styles.scoreSel1, styles.scoreSel2, styles.scoreSel3][opt.value]
                  : '';
                return (
                  <button key={opt.value} className={`${styles.scoreBtn} ${selCls}`}
                    onClick={() => setGsssAnswers(p => ({ ...p, [q.id]: opt.value }))}
                    aria-label={`${isID ? q.text_id : q.text_en} — ${opt.value}`}>
                    {opt.value}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <NavRow current="gsss" complete={gsssComplete} />
    </div>
  );

  // ===================== FLACC =====================
  if (step === 'flacc') return (
    <div className={styles.page}>
      <PublicBanner />
      <StepIndicator current="flacc" />
      <h1 className={styles.title}>
        <Activity size={24} aria-hidden="true" />
        {isID ? 'Tingkat Nyeri (FLACC)' : 'Pain Level (FLACC)'}
      </h1>

      <div className={styles.infoBox}>
        <div className={styles.infoTitle}><Info size={15} aria-hidden="true" />
          {isID ? 'Cara Mengisi' : 'How to Fill This Out'}</div>
        <p className={styles.infoText}>
          {isID
            ? 'Isi saat anak tampak tidak nyaman, kesakitan, atau rewel — bukan kondisi normalnya. Amati lalu pilih deskripsi yang paling sesuai untuk tiap dimensi.'
            : 'Fill out when the child appears uncomfortable, in pain, or fussy — not their normal state. Observe, then pick the best description for each dimension.'}
        </p>
        <p className={styles.sourceNote}>
          <BookOpen size={13} aria-hidden="true" />
          {isID ? 'Referensi: Merkel et al. (1997)' : 'Reference: Merkel et al. (1997)'}
        </p>
      </div>

      <div className={styles.questionsCard}>
        {FLACC_CATEGORIES.map(cat => (
          <div key={cat.id} className={styles.flaccItem}>
            <div className={styles.flaccLabel}>{isID ? cat.label_id : cat.label_en}</div>
            <div className={styles.flaccOptions}>
              {cat.options.map(opt => {
                const sel = flaccAnswers[cat.id] === opt.value;
                return (
                  <button key={opt.value}
                    className={`${styles.flaccBtn} ${sel ? styles.flaccBtnSel : ''}`}
                    onClick={() => setFlaccAnswers(p => ({ ...p, [cat.id]: opt.value }))}>
                    <span className={styles.flaccScore}>{opt.value}</span>
                    <span className={styles.flaccText}>{isID ? opt.label_id : opt.label_en}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <NavRow current="flacc" complete={flaccComplete} />
    </div>
  );

  // ===================== RESULT =====================
  const { giScore, gsssScore, flaccScore } = result;
  const cards = [
    { key: 'gi', label: isID ? 'Skrining Awal' : 'Initial Screening', score: giScore, max: 12,
      interp: getGIInterp(giScore, isID),
      desc: isID ? 'Faktor risiko Gangguan Pencernaan' : 'GI risk factors' },
    { key: 'gsss', label: isID ? 'Tingkat Keparahan Gejala Pencernaan' : 'GI Symptom Severity', score: gsssScore, max: 21,
      interp: getGSSSInterp(gsssScore, isID),
      desc: isID ? 'Keparahan gejala (7 hari)' : 'Symptom severity (7 days)' },
    { key: 'flacc', label: isID ? 'Tingkat Nyeri & Ketidaknyamanan' : 'Pain & Discomfort Level', score: flaccScore, max: 10,
      interp: getFLACCInterp(flaccScore, isID),
      desc: isID ? 'Tingkat nyeri (observasi)' : 'Pain level (observation)' },
  ];
  const followUp = getFollowUp({ giScore, gsssScore, flaccScore }, isID);

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>
        <BarChart2 size={26} aria-hidden="true" />
        {isID ? 'Hasil Skrining' : 'Screening Results'}
      </h1>

      {/* kartu skor */}
      <div className={styles.resultGrid}>
        {cards.map(c => (
          <div key={c.key} className={`${styles.resultCard} ${toneClass(c.interp.tone)}`}>
            <div className={styles.resultCardTitle}>{c.label}</div>
            <div className={styles.resultScore}>
              {c.score}<span className={styles.resultMax}>/{c.max}</span>
            </div>
            <div className={styles.resultLevel}>{c.interp.level}</div>
            <div className={styles.resultDesc}>{c.desc}</div>
          </div>
        ))}
      </div>

      {/* satu rekomendasi (berjenjang eksklusif) */}
      <div className={`${styles.followUp} ${toneClass(followUp.tone)}`}>
        <div className={styles.followUpTitle}>{followUp.title}</div>
        <div className={styles.followUpText}>{followUp.text}</div>
      </div>

      {/* disclaimer + rujukan */}
      <div className={styles.disclaimer}>
        <p className={styles.disclaimerText}>
          {isID
            ? 'Hasil ini BUKAN diagnosis medis, melainkan alat bantu awal. Selalu konsultasikan dengan dokter anak atau tenaga medis berkualifikasi.'
            : 'These results are NOT a medical diagnosis, only an initial tool. Always consult a pediatrician or qualified professional.'}
        </p>
        <div className={styles.refs}>
          <div className={styles.refsTitle}>
            <BookOpen size={15} aria-hidden="true" />
            {isID ? 'Rujukan Ilmiah' : 'Scientific References'}
          </div>
          <ul className={styles.refsList}>
            {REFERENCES.map((ref, i) => (
              <li key={i} className={styles.refItem}>
                <span aria-hidden="true">{i + 1}.</span>
                <a className={styles.refLink} href={ref.url} target="_blank" rel="noreferrer">
                  {isID ? ref.label_id : ref.label_en}
                  <ExternalLink size={12} aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* CTA daftar — hanya mode publik */}
      {isPublic && (
        <div className={styles.ctaCard}>
          <div className={styles.ctaText}>
            <div className={styles.ctaTitle}>
              {isID ? 'Simpan hasil & pantau perkembangan anak' : 'Save results & track your child\u2019s progress'}
            </div>
            <div className={styles.ctaDesc}>
              {isID
                ? 'Daftar gratis untuk menyimpan riwayat skrining, mencatat GI Log harian, dan melihat grafik perkembangan.'
                : 'Sign up free to save screening history, record daily GI logs, and view progress charts.'}
            </div>
          </div>
          <div className={styles.ctaActions}>
            <button className={styles.ctaBtn} onClick={onRegister}>
              {isID ? 'Daftar Gratis' : 'Sign Up Free'}
            </button>
            <button className={styles.ctaBtnSecondary} onClick={onLogin}>
              {isID ? 'Sudah punya akun' : 'Already have an account'}
            </button>
          </div>
        </div>
      )}

      <button className={styles.retakeBtn} onClick={reset}>
        <RefreshCw size={16} aria-hidden="true" />
        {isID ? 'Ulangi Tes' : 'Retake Test'}
      </button>
    </div>
  );
}

export default ScreeningFlow;